import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import hooks from '@g4/automation-hooks';

import { Input, baseStyles } from '../Input';

export const propTypes = {
  /** Associates a specific action onto an element for the benefit of automated clients consuming it */
  automationHook: PropTypes.string,
  /** Determines where delimiters are placed */
  groupLengths: PropTypes.array.isRequired,
  /** Character string used to break up input value into subgroups */
  delimiter: PropTypes.string,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
};

export const defaultProps = {
  delimiter: '-',
  onChange: Function.prototype,
  onFocus: Function.prototype,
  onBlur: Function.prototype,
};

/** @visibleName Masked Numeric Input */
export class MaskedNumericInput extends Input {
  constructor(props) {
    super(props);

    this.convert = this.convert.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.handleBlur = this.handleBlur.bind(this);

    const value = this.convert(props.value || '');

    this.state = {
      value,
      beforeChange: value,
    };
  }

  static propTypes = propTypes;
  static defaultProps = defaultProps;

  get value() {
    return (
      this.state.value.replace(new RegExp(this.props.delimiter, 'g'), '') || ''
    );
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.value !== this.value) {
      const value = this.convert(nextProps.value);

      this.setState({
        value,
      });
    }
  }

  // Todo: Backspace doesn't allow you to enter a previous group
  convert(value = '') {
    const { delimiter, groupLengths } = this.props;

    // Remove non-numeric characters
    const unformatted = value.replace(/[^0-9]+/g, '');

    var formatted = '';

    var start = 0;
    for (let i = 0; i < groupLengths.length; i++) {
      const groupLength = parseInt(groupLengths[i], 10);
      const chunk = unformatted.substring(start, start + groupLength);

      formatted += chunk;

      // Not last group
      if (i !== groupLengths.length - 1) {
        // Last character of group was entered
        if (chunk.length === groupLength) {
          // Insert delimiter character
          formatted += delimiter;
        }
      }

      start += groupLength;
    }

    if (formatted.charAt(formatted.length - 1) === delimiter) {
      let formattedWithoutLastDash = formatted.slice(0, formatted.length - 1);
      return formattedWithoutLastDash;
    }
    return formatted;
  }

  handleChange(event) {
    const { selectionStart } = this.el;
    const { delimiter } = this.props;
    const { beforeChange } = this.state;

    var value = event.target.value;
    var newSelectionStart = selectionStart;

    // Determine whether we're adding or removing characters by comparing the value's at
    // `onKeyDown` and `onChange`
    const backspace = beforeChange.length > value.length;
    const deleteBeyondGroup =
      backspace && beforeChange.charAt(selectionStart) === delimiter;

    // GMOB-732: Allow the backspace key to delete characters beyond the bounds of groups
    if (deleteBeyondGroup) {
      // Remove delimiter and last character of the group
      value =
        value.substring(0, selectionStart - 1) +
        value.substring(selectionStart);
    }

    const formatted = this.convert(value);

    if (
      !backspace &&
      (formatted.charAt(selectionStart) === delimiter ||
        formatted.charAt(selectionStart - 1) === delimiter)
    ) {
      // Update the caret position by accounting for the injected delimiter character
      newSelectionStart++;
    }

    this.setState({ value: formatted }, () => {
      this.props.onChange(this.value);

      // Without this, the caret will always reset to the end of the input
      // GMOB-773: `setTimeout` fixes Android not applying `setSelectionRange`
      setTimeout(() => {
        // GMOB2-99: The caret should always be right aligned of the delimiter
        this.el.setSelectionRange(newSelectionStart, newSelectionStart);
      });
    });
  }

  onKeyDown(event) {
    // Don't rely on `event.which` or `event.keyCode` since they're not detected correctly
    // on Android https://bugs.chromium.org/p/chromium/issues/detail?id=118639

    this.setState({
      beforeChange: this.state.value,
    });
  }

  handleBlur(event) {
    this.props.onBlur(event.target.value);
  }

  render() {
    return (
      <input
        {...this.attrs}
        ref={this.toRef}
        className={this.props.className}
        // GMOB2-227: Changed from type 'text' since otherwise Android doesnt correctly
        // read `HTMLInputElement.prototype.selectionStart`. One day the `inputmode` attribute
        // will be supported.
        type="tel"
        value={this.state.value}
        onChange={this.handleChange}
        onKeyDown={this.onKeyDown}
        onFocus={this.props.onFocus}
        onBlur={this.handleBlur}
        {...hooks.hook(this.props.automationHook)}
      />
    );
  }
}

export default styled(MaskedNumericInput)`
  ${baseStyles};
`;
